<template>
  <div class="app-container">
    <transition name="fade">
      <div v-if="isAppLoading">
        <SplashLoader />
      </div>
    </transition>

    <router-view v-show="!isAppLoading" />

    <CookieBanner v-if="!isAppLoading" />

    <RateLimitModal v-if="!isAppLoading" />
  </div>
</template>

<script setup>

import { ref, onMounted } from "vue";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import OnlineStatusService from "./api/OnlineStatusService";
import SplashLoader from "./components/SplashLoader.vue";
import RateLimitModal from "./components/RateLimitOverlay.vue";
import CookieBanner from "./views/CookieBanner.vue";

const theme = ref(localStorage.getItem("theme") || "light");
const isAppLoading = ref(true);

const setTheme = (newTheme) => {
  theme.value = newTheme;
  document.documentElement.setAttribute("data-theme", newTheme);
  localStorage.setItem("theme", newTheme);
};

onMounted(() => {
  setTheme(theme.value);

  const auth = getAuth();

  onAuthStateChanged(auth, async (user) => {
    if (user) {
      await OnlineStatusService.setOnlineStatus(true);
    }
    isAppLoading.value = false;
  });

  window.addEventListener("beforeunload", () => {
    navigator.sendBeacon(
      "https://europe-west3-hyper-e122e.cloudfunctions.net/update_online_status",
      JSON.stringify({ online: false })
    );
  });
});
</script>

<style>

:root {
  --profile_hashtag-search-bg: #f5f5f5;
  --header-bg-dark: #161616;
  --header-bg-light: #FFFFFF;
  --header-text-dark: white;
  --header-text-light: black;
  --separator-dark: #424242;
  --separator-light: #E7E7E7;
  --card-bg-dark: #2F2F2F;
  --card-bg-light: #F5F5F5;
  --card-hover-dark: #3A3A3A;
  --card-hover-light: #EAEAEA;
  
  --text-color-dark: white;
  --text-color-light: black;
  --text-muted-dark: #A09D9D;
  --text-muted-light: #666;

  --bg-color: #161616;
  --text-color: white;
  --secondary-text-color: #A09D9D;
  --primary-color: #2173E5;
  --rater-card-bg-dark: #3A3A3A;
  --rater-card-bg-light: #f0f0f0;
  --separator-color: #252525;

  --profile_background-color: #f2f2f2;
  --profile_container-color: #ffffff;
  --profile_text-color: #222;
  --profile_input-border: #ccc;
  --profile_input-active-border: #2173e5;
  --profile_switch-bg: #ffffff;
  --profile_switch-checked-bg: #2173e5;
  --profile_badge-border: #ddd;
  --profile_badge-hover: #f6f6f6;

  --profile_header-bg: #ffffff;
  --profile_footer-bg: #ffffff;
  --profile_hashtag-bg: #919191;

  --progress-registration-bg-dark: #2B2B2B;
  --progress-registration-bg-light: #e0e0e0;

  --switch-registration-bg-dark: #4C4B4B;
  --switch-registration-bg-light: #cbcbcb;

  --hashtag-background-registration-light: #f5f5f5;
  --hashtag-background-registration-dark: #3B3B3B;

  --chat-placeholder-bg: #ffffff;
  --chat-placeholder-text: #666666;

  --sidebar-background: #ffffff;
  --input-background: #f1f1f1;

  --badge-bg-light: #f0f2f5;
  --badge-text-light: #000;

  --badge-bg-dark: #2c2c2c;
  --badge-text-dark: #fff;

  --dropdown-bg-light: white;
  --dropdown-bg-dark: #2B2B2B;
  --dropdown-item-hover-light: #f5f5f5;
  --dropdown-item-hover-dark: #3A3A3A;
  --dropdown-border-light: #ddd;
  --dropdown-border-dark: #444;
  --request-hover-bg: #f5f5f5; 

  --chat-bg: #ffffff;
  --chat-incoming-bg: #f2f2f2;
  --chat-input-border: #e0e3ea;
  --icon-opacity: 0.8;
  --chat-call-bg: #f0f6ff;

  --sidebar-bg: #ffffff;
  --sidebar-separator: #e1e1e1;
  --sidebar-border: #dddddd;
  --dropdown-bg: #ffffff;
  --dropdown-hover: #f5f5f5;
  --modal-bg: #ffffff;
  --modal-title: #333333;
  --modal-text: #666666;
  --modal-cancel-bg: #f0f0f0;
  --modal-cancel-text: #333;
  --skeleton-bg: #eeeeee;
  --skeleton-shimmer: #dddddd;
  --chat-call-text: black;
  --chat-call-hover-bg: #e1efff;
  --progress-registration-bg: var(--progress-registration-bg-light);
  --input-border: #ccc;
  --currency-selector-bg: #f7f9fc;
  --currency-button-text: #999;
  --currency-active-bg: #fff;
  --currency-active-text: #000;
  --specific-selected-bg: #f0f8ff;
  --specific-selected-border: #2277E6;

  --toggle-bg-light: #ccc;
  --toggle-bg-dark: #444;

  --input-bg-light: #f0f0f0;
  --input-bg-dark: #1e1e1e;

  --input-text-light: #000;
  --input-text-dark: #fff;

  --toggle-slider-bg: #ccc;
  --address-input-bg: #f0f0f0;
  --address-input-text: #000;

  --upload-bg-light: #f6f6f6;
  --upload-bg-dark: #1e1e1e;
  --border-upload-light: #ccc;
  --border-upload-dark: #444;

  --my-vacancy-card-bg-light: #f5f5f5;
  --my-vacancy-card-bg-dark: #3c3c3c;

  --dropdown-bg: #f5f5f5;
  --dropdown-list-bg: #ffffff;
  --dropdown-hover-bg: #f0f0f0;
  --dropdown-active-bg: #e0e0e0;

  --hashtag-button-bg: #f5f5f5;
  --hashtag-button-bg-hover: #ececec;

   --dropdown-border-color: #ddd;
   --investor-bg: #eaf2ff;

  --search-bar-bg-dark: #3B3B3B;
  --search-input-color-dark: #FFFFFF;
  --search-placeholder-color-dark: #B0B0B0;

  --search-bar-bg-light: #E8E8E8;
  --search-input-color-light: #333333;
  --search-placeholder-color-light: #888888;

  --company-bg: #f9f9f9;
  --company-bg-hover: #f2f2f2;
  --company-name-color: #1a1a1a;
  --company-subtext-color: #868686;

  --drawer-bg: #ffffff;
  --drawer-text: #000000;
  --drawer-border: #e0e0e0;
  --region-bg: #ffffff;

  --text: #000000;
  --primary-color: #2173e5;
  --dropdown-border: #e0e0e0;

  --skeleton-bg: #e0e0e0;
  --skeleton-shimmer: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  font-family: "Eastman-Regular", sans-serif;
  transition: background-color 0.3s ease-in-out;
}

:root[data-theme="dark"] {
  --skeleton-bg: #444444;
  --skeleton-shimmer: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
  --search-bar-bg-dark: #3B3B3B;
  --search-input-color-dark: #FFFFFF;
  --search-placeholder-color-dark: #B0B0B0;
  --investor-bg: #293240;
  --dropdown-border-color: #444;
}

[data-theme="dark"] {
  --text: #ffffff;
  --primary-color: #4f9fff;
  --dropdown-border: #3c3c3c;
  --drawer-bg: #1d1d1d;
  --drawer-text: #ffffff;
  --drawer-border: #3a3a3a;
  --region-bg: #2a2a2a;
  --company-bg: #353535;
  --company-bg-hover: #404040;
  --company-name-color: #ffffff;
  --company-subtext-color: #bcbcbc;
  --hashtag-button-bg: #35383e;
  --hashtag-button-bg-hover: #2c2b2b;
}

[data-theme="dark"] {
  --dropdown-bg: #2a2a2a;
  --dropdown-list-bg: #1e1e1e;
  --dropdown-hover-bg: #333;
  --dropdown-active-bg: #444;
}

[data-theme="light"] {
  --my-vacancy-card-bg: var(--my-vacancy-card-bg-light);
}

[data-theme="dark"] {
  --my-vacancy-card-bg: var(--my-vacancy-card-bg-dark);
}

[data-theme="dark"] {
  --toggle-slider-bg: #555;
  --address-input-bg: #2a2a2a;
  --address-input-text: #fff;
}

:root[data-theme='dark'] {
  --specific-selected-bg: #1e2a36;
  --specific-selected-border: #2277E6;
}

:root[data-theme="dark"] {
  --currency-selector-bg: #2B2B2B;
  --currency-button-text: #888;
  --currency-active-bg: #3A3A3A;
  --currency-active-text: #fff;
}

:root[data-theme='dark'] {
  --input-border: #444;
}

html[data-theme='dark'] {
  --progress-registration-bg: var(--progress-registration-bg-dark);
}

[data-theme='dark'] {
  --dropdown-bg: var(--dropdown-bg-dark);
}

[data-theme="dark"] {
  --sidebar-bg: #161616;
  --sidebar-separator: #373737;
  --sidebar-border: #444;
  --dropdown-bg: #2b2b2b;
  --dropdown-hover: #3a3a3a;
  --modal-bg: #2a2a2a;
  --modal-title: #ffffff;
  --modal-text: #cccccc;
  --modal-cancel-bg: #3a3a3a;
  --modal-cancel-text: #f0f0f0;
  --skeleton-bg: #2f2f2f;
  --skeleton-shimmer: #3c3c3c;
}

[data-theme='dark'] {
  --chat-call-text: white;
  --chat-call-bg: #4d5396;
  --chat-call-hover-bg: #3b3f45;
}

[data-theme='dark'] {
  --icon-opacity: 1;
}

[data-theme='dark'] {
  --chat-input-border: #444;
}

[data-theme='dark'] {
  --chat-incoming-bg: #2a2a2a;
}

[data-theme='dark'] {
  --chat-bg: #161616;
}

[data-theme='dark'] {
  --request-hover-bg: #2a2a2a;
}

[data-theme='light'] {
  --dropdown-bg: var(--dropdown-bg-light);
  --dropdown-item-hover: var(--dropdown-item-hover-light);
  --dropdown-border: var(--dropdown-border-light);
}

[data-theme='dark'] {
  --dropdown-bg: var(--dropdown-bg-dark);
  --dropdown-item-hover: var(--dropdown-item-hover-dark);
  --dropdown-border: var(--dropdown-border-dark);
}

[data-theme='light'] {
  --badge-bg: var(--badge-bg-light);
  --badge-text: var(--badge-text-light);
}

[data-theme='dark'] {
  --badge-bg: var(--badge-bg-dark);
  --badge-text: var(--badge-text-dark);
}

[data-theme="dark"] {
  --sidebar-background: #161616;
  --input-background: #2b2b2b;
}

:root[data-theme='dark'] {
  --chat-placeholder-bg: #161616;
  --chat-placeholder-text: #ffffff;
}

/* ✅ Темы */
[data-theme="dark"] {
  background-color: #161616;
  color: white;
}

/* ✅ Темы */
[data-theme="dark"] {
  --profile_hashtag-bg: #3A3A3A;
}

[data-theme="light"] {
  background-color: #ffffff;
  color: black;
}

/* src/assets/variables.css */

:root {
  --separator_home: #E3E3E3;
}

[data-theme="light"] {
  --separator_home: #E3E3E3;
}

[data-theme="dark"] {
  --profile_hashtag-search-bg: #353535;
}

[data-theme="dark"] {
  --separator_home: #373737;
}

:root {
  --settings-drop-down-dark: #2B2B2B;
  --settings-drop-down-light: #FFFFFF;
}

/* Темная тема */
[data-theme="dark"] {
  --settings-drop-down: var(--settings-drop-down-dark);
  --profile_background-color: #121212;
  --profile_container-color: #1e1e1e;
  --profile_text-color: #eee;
  --profile_input-border: #555;
  --profile_input-active-border: #539bf5;
  --profile_switch-bg: #ffffff;
  --profile_switch-checked-bg: #539bf5;
  --profile_badge-border: #444;
  --profile_badge-hover: #2a2a2a;

  --profile_header-bg: #1a1a1a;
  --profile_footer-bg: #1a1a1a;
}

/* Светлая тема */
[data-theme="light"] {
  --settings-drop-down: var(--settings-drop-down-light);
}

/* ✅ Контейнер */
.app-container {
  min-height: 100vh;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>